import { useEffect, useState } from 'react';

interface LogoutModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
}

export default function LogoutModal({
  isOpen,
  onClose,
  onConfirm,
}: LogoutModalProps) {
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isLoading) {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, isLoading, onClose]);

  if (!isOpen) return null;

  const handleConfirm = async () => {
    try {
      setIsLoading(true);
      await onConfirm();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[9999] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200"
      onClick={!isLoading ? onClose : undefined}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-[#FDFFFC] rounded-3xl border border-gray-200 shadow-xl overflow-hidden animate-in zoom-in-95 duration-200"
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          disabled={isLoading}
          className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-xl hover:bg-slate-100 transition-all disabled:opacity-50"
        >
          <i className="ri-close-line text-lg text-slate-500"></i>
        </button>

        {/* Header */}
        <div className="px-8 pt-8 pb-6">
          <div className="w-14 h-14 rounded-2xl bg-[#0859B8]/10 flex items-center justify-center mb-5 border border-[#0859B8]/20">
            <i className="ri-logout-box-r-line text-2xl text-[#0859B8]"></i>
          </div>

          <h2 className="text-xl font-bold text-[#222222]">
            Logout
          </h2>

          <p className="mt-2 text-sm text-[#222222]/70 leading-relaxed font-medium">
            You are about to sign out of your account.
            Make sure all important changes have been saved before continuing.
          </p>
        </div>

        {/* Footer */}
        <div className="px-8 pb-8 flex gap-3">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="
              flex-1
              py-3
              rounded-xl
              border-2
              border-gray-200
              bg-[#FDFFFC]
              text-[#222222]
              font-bold
              hover:bg-gray-100
              transition-all
              disabled:opacity-50
              disabled:cursor-not-allowed
            "
          >
            Cancel
          </button>

          <button
            onClick={handleConfirm}
            disabled={isLoading}
            className="
              flex-1
              py-3
              rounded-xl
              bg-[#0859B8]
              text-[#FDFFFC]
              font-bold
              hover:bg-[#064893]
              transition-all
              disabled:opacity-70
              disabled:cursor-not-allowed
              flex
              items-center
              justify-center
              gap-2
            "
          >
            {isLoading && (
              <i className="ri-loader-4-line animate-spin"></i>
            )}

            {isLoading ? 'Logging out...' : 'Logout'}
          </button>
        </div>
      </div>
    </div>
  );
}